/* ════════════════════════════════════════════════════════════════════════
   Food Intelligence — derived Family → Kitchen context.
   The Family Profile (member.food.*) is the single source of truth.
   Nothing here is persisted; everything is calculated from members.
   Priority: allergy > intolerance / hard dietary > won't eat > sensory
             > dislike > preference > favourite.
   ════════════════════════════════════════════════════════════════════════ */

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const norm = (s) => String(s || "").toLowerCase().trim();
const singular = (s) => (s.endsWith("es") && s.length > 4 ? s.slice(0, -2) : s.endsWith("s") && s.length > 3 ? s.slice(0, -1) : s);

/* Common ingredient groups — deliberately modest, not clinically complete. */
const INGREDIENT_GROUPS = {
  peanut: ["peanut", "peanuts", "peanut butter", "satay", "groundnut"],
  "tree nut": ["tree nut", "almond", "cashew", "walnut", "pecan", "hazelnut", "pistachio", "macadamia"],
  dairy: ["dairy", "milk", "cream", "creamy", "cheese", "butter", "yoghurt", "yogurt", "ghee", "custard", "ice cream", "parmesan", "mozzarella", "feta", "halloumi"],
  egg: ["egg", "eggs", "mayonnaise", "mayo", "aioli"],
  gluten: ["gluten", "wheat", "flour", "bread", "breadcrumbs", "pasta", "spaghetti", "noodle", "noodles", "naan", "couscous", "barley"],
  fish: ["fish", "salmon", "tuna", "cod", "snapper", "anchovy", "anchovies", "sardine", "trout", "hoki", "tarakihi"],
  shellfish: ["shellfish", "prawn", "shrimp", "crab", "lobster", "mussel", "oyster", "scallop", "squid", "calamari"],
  soy: ["soy", "soya", "tofu", "edamame", "soy sauce"],
  sesame: ["sesame", "tahini"],
};

/* Hard dietary rules — cautious, only unmistakable diets. */
const DIET_RULES = {
  vegetarian: ["chicken", "beef", "lamb", "pork", "bacon", "ham", "sausage", "mince", "steak", "turkey", "duck", "fish", "salmon", "tuna", "prawn", "shrimp"],
  vegan: ["chicken", "beef", "lamb", "pork", "bacon", "ham", "sausage", "mince", "steak", "turkey", "duck", "fish", "salmon", "tuna", "prawn", "shrimp", ...INGREDIENT_GROUPS.dairy, "egg", "eggs", "honey"],
  pescatarian: ["chicken", "beef", "lamb", "pork", "bacon", "ham", "sausage", "mince", "steak", "turkey", "duck"],
  "gluten free": INGREDIENT_GROUPS.gluten,
  "gluten-free": INGREDIENT_GROUPS.gluten,
  "dairy free": INGREDIENT_GROUPS.dairy,
  "dairy-free": INGREDIENT_GROUPS.dairy,
};

/* Expand a saved preference term into cautious match variants. */
function termVariants(term) {
  const t = norm(term);
  if (!t) return [];
  const variants = new Set([t, singular(t)]);
  Object.entries(INGREDIENT_GROUPS).forEach(([key, list]) => {
    const bucket = [key, ...list.map(norm)];
    if (bucket.includes(t) || bucket.includes(singular(t))) {
      bucket.forEach(v => variants.add(v));
    }
  });
  return [...variants];
}

/* Non-dairy phrases that must not trigger dairy variants. */
const EXCLUSIONS = {
  milk: /\b(coconut|almond|oat|soy|soya|rice|cashew)\s+milk/gi,
  butter: /\b(peanut|almond|cashew|nut|seed)\s+butter/gi,
  cream: /\bcoconut\s+cream/gi,
};

function findMatch(text, variants) {
  for (const v of variants) {
    const scanText = EXCLUSIONS[v] ? text.replace(EXCLUSIONS[v], " ") : text;
    if (new RegExp(`\\b${escapeRe(v)}`, "i").test(scanText)) return v;
  }
  return null;
}

export const firstNameOf = (m) => ((m?.preferredName || m?.name || "").trim().split(" ")[0] || "Someone");

/* ── Derived household food context ─────────────────────────────────
   selectedMemberIds: [] or null = whole household. */
export function getHouseholdFoodContext(members, selectedMemberIds) {
  const all = members || [];
  const validIds = new Set(all.map(m => m.id));
  const selected = (selectedMemberIds || []).filter(id => validIds.has(id));
  const active = selected.length > 0 ? all.filter(m => selected.includes(m.id)) : all;

  const collect = (field) => {
    const map = new Map();
    active.forEach(m => {
      (m.food?.[field] || []).forEach(raw => {
        const key = norm(raw);
        if (!key) return;
        if (!map.has(key)) map.set(key, { term: String(raw).trim(), members: [] });
        const entry = map.get(key);
        const name = firstNameOf(m);
        if (!entry.members.includes(name)) entry.members.push(name);
      });
    });
    return [...map.values()];
  };

  const ctx = {
    scope: selected.length > 0 ? "selected" : "household",
    memberIds: active.map(m => m.id),
    memberNames: active.map(firstNameOf),
    allergies: collect("allergies"),
    intolerances: collect("intolerances"),
    dietaryPreferences: collect("dietary"),
    wontEat: collect("wontEat"),
    sensoryPreferences: collect("textures"),
    dislikes: collect("dislikes"),
    favourites: [...collect("favouriteFoods"), ...collect("likes")],
    favouriteMeals: collect("favouriteMeals"),
    snacks: collect("snacks"),
    lunchboxFavourites: collect("lunchbox"),
    takeawayPreferences: collect("takeaway"),
  };
  ctx.hasAnyFoodInfo = [
    ctx.allergies, ctx.intolerances, ctx.dietaryPreferences, ctx.wontEat,
    ctx.sensoryPreferences, ctx.dislikes, ctx.favourites, ctx.favouriteMeals,
    ctx.snacks, ctx.lunchboxFavourites, ctx.takeawayPreferences,
  ].some(list => list.length > 0);
  return ctx;
}

const joinNames = (names) => names.length <= 1 ? names[0]
  : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

/* ── Recipe / meal evaluation ───────────────────────────────────────
   Input: { recipe, title } — recipe optional; title used when no recipe. */
export function evaluateMealForHousehold({ recipe, title }, ctx) {
  const empty = { status: "none", line: null, conflicts: [], checks: [], positives: [] };
  if (!ctx || !ctx.hasAnyFoodInfo) return empty;
  const text = [
    title, recipe?.title, recipe?.subtitle,
    ...(recipe?.ingredients || []),
  ].filter(Boolean).join(" \n ").toLowerCase();
  if (!text.trim()) return empty;

  const conflicts = [];
  const checks = [];
  const positives = [];

  const scan = (entries, kind, into) => {
    entries.forEach(({ term, members }) => {
      const matched = findMatch(text, termVariants(term));
      if (matched) into.push({ kind, term: norm(term), matched, members });
    });
  };

  scan(ctx.allergies, "allergy", conflicts);
  scan(ctx.intolerances, "intolerance", conflicts);
  (ctx.dietaryPreferences || []).forEach(({ term, members }) => {
    const rule = DIET_RULES[norm(term)];
    if (!rule) return;
    const matched = findMatch(text, rule.map(norm));
    if (matched) conflicts.push({ kind: "dietary", term: norm(term), matched, members });
  });
  scan(ctx.wontEat, "wontEat", checks);
  scan(ctx.sensoryPreferences, "sensory", checks);
  scan(ctx.dislikes, "dislike", checks);
  scan([...ctx.favourites, ...ctx.favouriteMeals], "favourite", positives);

  const status = conflicts.length > 0 ? "conflict" : checks.length > 0 ? "check" : "good";
  return { status, line: buildLine(status, conflicts, checks, positives), conflicts, checks, positives };
}

/* One calm, human sentence — safety first, then suitability, then warmth. */
function buildLine(status, conflicts, checks, positives) {
  if (status === "conflict") {
    const c = conflicts[0];
    const who = joinNames(c.members);
    if (c.kind === "allergy") return `Contains ${c.matched} — ${who} has ${c.term} listed under allergies.`;
    if (c.kind === "intolerance") return `This contains ${c.matched}, which ${who} has listed as an intolerance.`;
    return `${who} has ${c.term} listed as a dietary preference — ${c.matched} may not suit.`;
  }
  if (status === "check") {
    const c = checks[0];
    const who = joinNames(c.members);
    if (c.kind === "wontEat") return `${who} has ${c.term} listed under foods they won't eat.`;
    if (c.kind === "sensory") return `Worth a check — ${who} has ${c.term} noted under texture preferences.`;
    return `${who} doesn't usually go for ${c.term}.`;
  }
  if (positives.length > 1) return `${positives.length} of these are family favourites.`;
  if (positives.length === 1) return `${joinNames(positives[0].members)} loves ${positives[0].term} — a good sign.`;
  return "Looks good for everyone you're cooking for.";
}

/* Names for a subtle grid indicator — conflict-level only. */
export function conflictNames(evaluation) {
  if (!evaluation || evaluation.status !== "conflict") return [];
  const names = [];
  evaluation.conflicts.forEach(c => c.members.forEach(n => { if (!names.includes(n)) names.push(n); }));
  return names;
}
